/** Selects dump/slot/outro highlight targets during the welcome plan tour. */
import { isUnscheduledCandidate } from '@/features/tasks/utils/views/filtersViewUtils';
import { isSchedulableTask } from '@/features/tasks/utils/scheduling/taskSchedulingUtils';

/** @typedef {'context' | 'duration'} DumpHintKind */

/**
 * Task to highlight during the plan-tour slot: first schedulable next-action
 * (subtask), else first schedulable unscheduled task (hoofdtaak).
 * @param {Object} params
 * @param {import('@/types').Task[]} params.unscheduledTasks
 * @param {import('@/types').Task[]} [params.nextActions]
 * @param {Map<string, import('@/types').Task>} [params.tasksMap]
 * @returns {string | null}
 */
export function selectOnboardingSlotHighlightTaskId({
  unscheduledTasks,
  nextActions = [],
  tasksMap = new Map(),
}) {
  const map =
    tasksMap instanceof Map && tasksMap.size > 0
      ? tasksMap
      : new Map(
          [...(unscheduledTasks ?? []), ...(nextActions ?? [])].map((task) => [
            task.id,
            task,
          ]),
        );

  const pickSchedulableUnscheduled = (list) =>
    (list ?? []).find(
      (task) =>
        isUnscheduledCandidate(task) && isSchedulableTask(task, map),
    ) ?? null;

  const fromNext = pickSchedulableUnscheduled(nextActions);
  if (fromNext) {
    return fromNext.id;
  }

  return pickSchedulableUnscheduled(unscheduledTasks)?.id ?? null;
}

/**
 * @param {number} newDumpTaskCount Tasks added in Te plannen during part B
 * @returns {DumpHintKind | null}
 */
export function selectDumpHint(newDumpTaskCount) {
  if (newDumpTaskCount >= 3) {
    return 'duration';
  }
  if (newDumpTaskCount === 2) {
    return 'context';
  }
  return null;
}

/**
 * @param {import('@/types').Task[]} unscheduledTasks
 * @param {ReadonlySet<string>} baselineIds
 */
export function countNewDumpTasks(unscheduledTasks, baselineIds) {
  return (unscheduledTasks ?? []).filter(
    (task) =>
      isUnscheduledCandidate(task) && !baselineIds.has(task.id),
  ).length;
}

/**
 * @param {import('@/types').Task[]} unscheduledTasks
 * @returns {Set<string>}
 */
export function collectUnscheduledCandidateIds(unscheduledTasks) {
  const ids = new Set();
  for (const task of unscheduledTasks ?? []) {
    if (isUnscheduledCandidate(task)) {
      ids.add(task.id);
    }
  }
  return ids;
}

export function hasUnscheduledDumpTasks(unscheduledTasks) {
  return (unscheduledTasks ?? []).some((task) => isUnscheduledCandidate(task));
}

/** @typedef {'planned' | 'plannedWithMore' | 'unplanned'} OutroVariant */

/**
 * @param {Object} params
 * @param {string | null} slotTargetTaskId Highlight target from the slot phase
 * @param {import('@/types').Task[]} params.unscheduledTasks
 * @param {ReadonlySet<string>} params.baselineIds Unscheduled ids at invite entry
 * @param {Map<string, import('@/types').Task>} [params.tasksMap]
 * @param {import('@/types').Task[]} [params.nextActions]
 * @returns {OutroVariant}
 */
export function selectOutroVariant({
  slotTargetTaskId,
  unscheduledTasks,
  baselineIds,
  tasksMap = new Map(),
  nextActions = [],
}) {
  const resolveTask = (id) => {
    if (!id) {
      return null;
    }
    return (
      tasksMap.get(id) ??
      (unscheduledTasks ?? []).find((task) => task.id === id) ??
      (nextActions ?? []).find((task) => task.id === id) ??
      null
    );
  };

  const slotTarget = resolveTask(slotTargetTaskId);
  const firstStepPlanned =
    slotTarget !== null && !isUnscheduledCandidate(slotTarget);

  if (!firstStepPlanned) {
    return 'unplanned';
  }

  const addedDuringInvite = countNewDumpTasks(unscheduledTasks, baselineIds) > 0;
  const otherUnscheduled = (unscheduledTasks ?? []).some(
    (task) =>
      isUnscheduledCandidate(task) && task.id !== slotTargetTaskId,
  );

  if (addedDuringInvite || otherUnscheduled) {
    return 'plannedWithMore';
  }

  return 'planned';
}
